'use client';
import Button from '@ui/Button';
import Chip from '@ui/Chip';
import Link from '@ui/Link';
import Typography from '@ui/Typography';
import { getEndsInText } from '@utils/dateHelpers';
import {
  getInKindFundraiserLink,
  getPostImage,
  getPostLink,
  shouldDisablePostCauses,
} from '@utils/post.helper';
import useJoinNonProfitDialogStore from 'components/store/joinNonProfitDialogSlice';
import React from 'react';
import ActionBar from './ActionBar';
import InKindFundraiserGivingLevels from './InKindFundraiserGivingLevels';
import LatestInKindDonors from './LatestInKindDonors';
import NonProfitCauses from './NonProfitCauses';
import PostAuthorProfile from './PostAuthorProfile';
import PostImage from './PostImage';
import PostVideo from './PostVideo';
import PostMessage from './PostMessage';
import ReactionBar from './ReactionBar';
import useConnectModalStore from '@/components/store/connectModalSlice';
import OrgType from 'constants/orgType.constant';
import PostTypeIndicatorChip from './PostTypeIndicatorChip';
import DonateInKindDialog from '../DonateInKindDialog';
import useHasUserSession from '@/hooks/useHasUserSession';
import { useRouter } from 'next/navigation';
import useNonProfitSignUpPopUpStore from '../store/nonProfitSignUpPopUpSlice';
import useCompanyConnectPopUpStore from '../store/companyConnectPopUpSlice';
import useUserProfile from '@/hooks/useUserProfile';
import { recordDonationClick } from '@/actions/public/server.actions';
import TrackPostVisibility from '../TrackPost/trackPostVisibility';
import NonProfitProfileBar from './NonProfitProfileBar';
import PostImageLink from './PostImageLink';

const getGivingLevels = (project) => {
  if (project) {
    return project.givingLevels.map((level) => {
      return {
        id: level.id,
        title: level.title,
        quantity: level.quantity,
        description: level.description,
      };
    });
  }
  return [];
};

function InKindFundraiserPostBody(props) {
  const { post, type, home, orgSlug = null } = props;
  const { sourcePostData } = post;
  const nonProfit = post.renderProps.nonProfit;

  const hasUserSession = useHasUserSession();
  const user = useUserProfile();
  const router = useRouter();

  const { title, message, endDate } = sourcePostData;

  const endsInDaysText = getEndsInText(endDate);

  const [showDonateDialog, setShowDonateDialog] = React.useState(false);

  const setShowJoinNonProfitModal = useJoinNonProfitDialogStore(
    (state) => state.setOpen,
  );
  const setShowConnectModal = useConnectModalStore((state) => state.setOpen);

  const setShowNonProfitSignUpPopUp = useNonProfitSignUpPopUpStore(
    (state) => state.setOpen,
  );
  const setShowCompanyConnectPopUp = useCompanyConnectPopUpStore(
    (state) => state.setOpen,
  );

  let recordDonationClickPayload = {
    type: 'post',
    postId: post.id,
    donationClick: {
      userId: user?.id,
      activeCommunityId: user?.activeCommunity?.id,
      location: user?.location,
    },
  };

  const handleDonate = () => {
    if (hasUserSession) {
      setShowDonateDialog(true);
      setTimeout(() => {
        recordDonationClick(recordDonationClickPayload);
      }, 2000);
    } else {
      if (
        [OrgType.Business, OrgType.ChangeMakers].includes(
          post.renderProps.company.orgType,
        )
      ) {
        // setShowConnectModal(true, post.renderProps.company);
        setShowCompanyConnectPopUp(true, post.renderProps.company);
      } else {
        // setShowJoinNonProfitModal(true, post.renderProps.nonProfit);
        // setShowJoinNonProfitModal(false, post.renderProps.nonProfit);
        // const returnPath = `/onboard/nonprofit-user?nonProfitId=${post.renderProps.nonProfit?.id}`;
        // router.replace(
        //   `/api/auth/signup?returnTo=${encodeURIComponent(returnPath)}`,
        // );
        setShowNonProfitSignUpPopUp(true, post.renderProps.nonProfit);
      }
    }
  };

  // const hasImage = post.renderProps ? getPostImage(post) : false;
  const hasVideo = post.renderProps?.videoUrls && post.renderProps.videoUrls[0];
  let slug = post.renderProps?.company?.name.toLowerCase().replace(/\s+/g, '-');

  slug =
    post.renderProps?.company?.orgType === OrgType.NonProfit
      ? `nonprofit/${slug}`
      : `company/${slug}`;

  const disablePostCauses = shouldDisablePostCauses(home, orgSlug);
  const postLink = getPostLink(post);

  return (
    <TrackPostVisibility post={post} type={type}>
      <div>
        <div className="divide-y px-6 lg:px-4">
          <div className="flex flex-col gap-4 py-6 lg:py-4">
            {!disablePostCauses && nonProfit && nonProfit.causes && (
              <NonProfitCauses
                nonProfit={nonProfit}
                className="border-b pb-4"
              />
            )}

            <PostAuthorProfile post={post} home={home} orgSlug={orgSlug} />
          </div>

          <div className="flex w-full flex-col items-start justify-between gap-2 py-2 lg:flex-row">
            <Link href={postLink}>
              <Typography
                variant="h5"
                className="flex-1 group-hover:text-text-hyperlink">
                {title}
              </Typography>
            </Link>
            <div className="flex w-full items-center justify-between gap-2 lg:w-auto lg:flex-col lg:items-end">
              <PostTypeIndicatorChip
                postType={post.sourcePostType}
                campaignType={post.sourcePostData.campaignType}
              />
              <Typography
                variant="caption"
                color="textSecondary"
                className="whitespace-nowrap">
                {endsInDaysText}
              </Typography>
            </div>
          </div>
        </div>

        {hasVideo ? (
          <PostVideo post={post} />
        ) : (
            <PostImageLink post={post} />
        )}

        {nonProfit && <NonProfitProfileBar nonProfit={nonProfit} post={post} />}

        <div className="mt-3 px-4">
          <InKindFundraiserGivingLevels
            post={post}
            className="overflow-hidden"
            paddingClass="px-6 lg:px-4"
          />
        </div>

        <div className="flex items-center px-6 py-4 lg:px-4">
          <LatestInKindDonors post={post} />
          {nonProfit && !nonProfit.blocked && (
            <>
              <DonateInKindDialog
                open={showDonateDialog}
                post={post}
                onClose={() => setShowDonateDialog(false)}
              />
              <Button
                variant="filled"
                color="primary"
                className="ml-auto"
                onClick={handleDonate}>
                Donate
              </Button>
            </>
          )}
        </div>

        <div className="flex flex-col gap-6 px-6 lg:px-4">
          <div className="divide-y">
            <div className="py-2">
              <PostMessage message={message} />
            </div>

            <ReactionBar post={post} />

            <div className="flex justify-center py-2">
              <ActionBar post={post} orgSlug={orgSlug} />
            </div>
          </div>
        </div>
      </div>
    </TrackPostVisibility>
  );
}

export default InKindFundraiserPostBody;
